import { useEffect, useState } from 'react';
import { selectorForTourTarget } from './tourTargets';

export const isTourTargetReady = (target?: string): boolean => {
  if (!target) return true;

  return (
    document.querySelector(selectorForTourTarget(target))?.getAttribute('data-tour-form-ready') ===
    'true'
  );
};

/**
 * childList/subtree observation can't be scoped to a selector, so every mutation
 * app-wide reaches the callback regardless of attributeFilter - this decides
 * whether a given mutation actually concerns the tour target before triggering
 * a re-check, instead of re-querying the document on unrelated DOM churn.
 */
const mutationTouchesTarget = (mutation: MutationRecord, selector: string): boolean => {
  if (mutation.type === 'attributes') {
    return mutation.target instanceof Element && mutation.target.matches(selector);
  }

  return [...mutation.addedNodes, ...mutation.removedNodes].some(
    (node) =>
      node instanceof Element && (node.matches(selector) || node.querySelector(selector) !== null),
  );
};

export const watchTourTargetReady = (target: string, onReady: () => void): (() => void) => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => undefined;
  }

  const selector = selectorForTourTarget(target);
  let isStopped = false;
  const observer = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) => mutationTouchesTarget(mutation, selector)) &&
      isTourTargetReady(target)
    ) {
      stop();
      onReady();
    }
  });
  const stop = (): void => {
    if (isStopped) return;
    isStopped = true;
    observer.disconnect();
  };

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-tour-form-ready'],
  });

  // Defer the initial check so callers can store the returned cleanup before
  // an already-ready target invokes the callback.
  queueMicrotask(() => {
    if (!isStopped && isTourTargetReady(target)) {
      stop();
      onReady();
    }
  });

  return stop;
};

export const useTourTargetReadiness = (target?: string): boolean => {
  const [isReady, setIsReady] = useState(() => isTourTargetReady(target));

  useEffect(() => {
    const updateReadiness = (): void => {
      setIsReady(isTourTargetReady(target));
    };

    updateReadiness();

    if (!target) return undefined;

    const selector = selectorForTourTarget(target);
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutationTouchesTarget(mutation, selector))) {
        updateReadiness();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tour-form-ready'],
    });

    return () => {
      observer.disconnect();
    };
  }, [target]);

  return isReady;
};
